import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { AppDispatch } from "../store";
import { categoryActions } from "../slices/category";
import { getErrorMessage } from "../../utils/errorUtils";
import { CategoryRequestDto } from "../../shared/dtos/category/CategoryRequestDto";
import { CategoryResponseDto } from "../../shared/dtos/category/CategoryResponseDto";
import { UpdateCategoryPayload } from "../../shared/interfaces/category/UpdateCategoryPayload";
import { PaginationQueryDto } from "../../shared/dtos/common/PaginationQueryDto";
import { ApiResponseDto } from "../../shared/dtos/common/ApiResponseDto";
import { PaginatedResponse } from "../../shared/dtos/common/PaginatedResponse";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";

export const getCategories =
  (params?: PaginationQueryDto) => async (dispatch: AppDispatch) => {
    dispatch(categoryActions.setLoadingFetchAll());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<PaginatedResponse<CategoryResponseDto>>
      >("/categories", { headers: { requiresAuth: true }, params });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(categoryActions.setItems(data.data.content));
        dispatch(categoryActions.setPagination(data.data.pagination));
      }
    } catch (error) {
      dispatch(categoryActions.setErrorFetchAll(getErrorMessage(error)));
    }
  };

export const getCategoryById =
  (id: string) => async (dispatch: AppDispatch) => {
    dispatch(categoryActions.setLoadingFetchOne());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<CategoryResponseDto>
      >(`/categories/${id}`, { headers: { requiresAuth: true } });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(categoryActions.setItem(data.data));
      }
    } catch (error) {
      dispatch(categoryActions.setErrorFetchOne(getErrorMessage(error)));
    }
  };

export const createCategory =
  (values: CategoryRequestDto) => async (dispatch: AppDispatch) => {
    dispatch(categoryActions.setLoadingCreate());
    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<CategoryResponseDto>
      >("/categories", values, { headers: { requiresAuth: true } });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Category created!");
        dispatch(categoryActions.createSuccess(data.data));
        dispatch(
          categoryActions.setServerStatus({ operation: "create", status: 201 }),
        );
      }
    } catch (error) {
      dispatch(categoryActions.setErrorCreate(getErrorMessage(error)));
    }
  };

export const updateCategory =
  (payload: UpdateCategoryPayload) => async (dispatch: AppDispatch) => {
    dispatch(categoryActions.setLoadingUpdate());
    try {
      const { data } = await axiosInstance.put<
        ApiResponseDto<CategoryResponseDto>
      >(`/categories/${payload.id}`, payload.values, {
        headers: { requiresAuth: true },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Category updated!");
        dispatch(categoryActions.updateSuccess(data.data));
      }
    } catch (error) {
      dispatch(categoryActions.setErrorUpdate(getErrorMessage(error)));
    }
  };

export const deleteCategory = (id: string) => async (dispatch: AppDispatch) => {
  dispatch(categoryActions.setLoadingDelete());
  try {
    const { data } = await axiosInstance.delete<ApiResponseDto<void>>(
      `/categories/${id}`,
      { headers: { requiresAuth: true } },
    );
    if (data.status === ApiResponseStatus.SUCCESS) {
      toast.success(data.message || "Category deleted.");
      dispatch(categoryActions.deleteSuccess(id));
    }
  } catch (error) {
    dispatch(categoryActions.setErrorDelete(getErrorMessage(error)));
  }
};

export const resetCategoryServerStatus = () => (dispatch: AppDispatch) =>
  dispatch(categoryActions.setServerStatus(null));
